import { CreateIndividualSchema, RelationSchema, TRIAL_INDIVIDUAL_LIMIT } from '@backend/schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import i18n from 'i18next'
import { InfoIcon, LogOutIcon, XIcon } from 'lucide-react'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { useCreateIndividual } from '@/api/hooks/useCreateIndividual'
import { useMe } from '@/api/hooks/useMe'
import { useMunicipalities } from '@/api/hooks/useMunicipalities'
import { MunicipalityInput } from '@/components/molecules/municipality-input'
import PidInput, { pidSchema } from '@/components/molecules/pid-input'
import { RelationFieldGroup } from '@/components/molecules/relation-field-group'
import { RELATION_OTHER_VALUE } from '@/components/molecules/relation-select'
import Wizard from '@/components/organisms/wizard'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { firebaseAuth } from '@/integrations/firebase'
import authenticationService from '@/services/authentication/authentication'

const schema = z
  .object({
    creatorIsGuardian: z.boolean().default(false),
    creatorIsLegalRepresentative: z.boolean().default(false),
    creatorRelation: RelationSchema.or(z.literal(RELATION_OTHER_VALUE)),
    // Not yet accepted by the backend — see RELATION_OTHER_VALUE.
    creatorRelationOther: z.string().max(200).optional(),
    dataConsent: z.literal(true, { error: () => i18n.t('formValidation.consentRequired') }),
    firstName: z.string().min(1).max(50),
    gender: CreateIndividualSchema.shape.gender,
    individualConsent: z.boolean().default(false),
    lastName: z.string().min(1).max(50),
    municipality: z.string(),
    pid: pidSchema,
    testPeriodConsent: z.literal(true, { error: () => i18n.t('formValidation.consentRequired') }),
  })
  // Only relevant when the creator isn't the individual themselves — "Jag
  // själv" means there's no separate person to consent on behalf of.
  .superRefine((data, ctx) => {
    if (data.creatorRelation !== 'self' && !data.individualConsent) {
      ctx.addIssue({
        code: 'custom',
        message: i18n.t('formValidation.consentRequired'),
        path: ['individualConsent'],
      })
    }

    if (data.creatorRelation === RELATION_OTHER_VALUE && !data.creatorRelationOther?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: i18n.t('formValidation.relationDescriptionRequired'),
        path: ['creatorRelationOther'],
      })
    }
  })

type Input = z.input<typeof schema>

const AddIndividualPage = () => {
  const navigate = useNavigate()
  const router = useRouter()
  const createIndividual = useCreateIndividual()
  const { data: user } = useMe()
  const { t } = useTranslation()

  const hasIndividuals = (user?.individuals.length ?? 0) > 0

  // Only unpaid individuals count toward the cap — paying for one frees a slot. The
  // server enforces this; showing it here avoids filling in the whole wizard first.
  const unpaidIndividuals = (user?.individuals ?? []).filter(
    individual => individual.subscription.status !== 'active',
  ).length
  const atTrialLimit = unpaidIndividuals >= TRIAL_INDIVIDUAL_LIMIT

  const onSubmit: SubmitHandler<Input> = async ({ creatorRelationOther, ...data }) => {
    // creatorRelationOther has no backend field yet, so it's captured but not sent.
    // creatorRelation "other" IS sent as-is — the backend enum will reject it until
    // it adds the value (see RELATION_OTHER_VALUE), surfacing via the mutation's error handling.
    void creatorRelationOther

    try {
      await createIndividual.mutateAsync({
        requestBody: {
          ...data,
          creatorIsGuardian: data.creatorIsGuardian ?? false,
          creatorIsLegalRepresentative: data.creatorIsLegalRepresentative ?? false,
          creatorRelation: data.creatorRelation as z.infer<typeof RelationSchema>,
        },
      })
    } catch (error) {
      // 402 means the trial individual cap was hit. The cap itself isn't exposed to
      // the client, so the server's message is the only place the number lives —
      // surface it rather than a generic failure.
      if ((error as { status?: number })?.status === 402) {
        toast.error(t('addIndividualPage.trialLimitTitle'), {
          description: t('addIndividualPage.trialLimitDescription', {
            limit: TRIAL_INDIVIDUAL_LIMIT,
          }),
        })
        return
      }
      throw error
    }

    await authenticationService.refreshIdToken()
    navigate({ replace: true, to: '/' })
  }

  return (
    <div className="p-4 md:pt-10 flex flex-col w-full items-center bg-background">
      <div className="w-[min(100%,_880px)] space-y-20">
        {hasIndividuals ? (
          <Button onClick={() => router.history.back()} variant="secondary">
            <XIcon />
            {t('global.cancel')}
          </Button>
        ) : (
          <Button onClick={() => firebaseAuth.signOut()} variant="ghost">
            <LogOutIcon />
            {t('global.signOut')}
          </Button>
        )}
        {atTrialLimit && (
          <Alert variant="info">
            <InfoIcon />
            <div>
              <AlertTitle>{t('addIndividualPage.trialLimitTitle')}</AlertTitle>
              <AlertDescription>
                {t('addIndividualPage.trialLimitDescription', {
                  limit: TRIAL_INDIVIDUAL_LIMIT,
                })}
              </AlertDescription>
            </div>
          </Alert>
        )}
        <Wizard<Input>
          formOptions={{
            defaultValues: {
              creatorIsGuardian: true,
              creatorIsLegalRepresentative: false,
              firstName: '',
              lastName: '',
              pid: '',
            },
            resolver: zodResolver(schema),
          }}
          onSubmit={onSubmit}
          steps={[
            {
              Component: PersonalInformationStep,
              fields: [
                'firstName',
                'lastName',
                'pid',
                'gender',
                'municipality',
                'creatorRelation',
                'creatorRelationOther',
                'creatorIsGuardian',
                'creatorIsLegalRepresentative',
              ],
              title: t('addIndividualPage.stepTitles.personalInfo'),
            },
            {
              Component: DataConsentStep,
              fields: ['dataConsent', 'testPeriodConsent', 'individualConsent'],
              title: t('addIndividualPage.stepTitles.dataConsent'),
            },
          ]}
          submitLabel={t('addIndividualPage.submitLabel')}
        />
      </div>
    </div>
  )
}

const DataConsentStep = () => {
  const form = useFormContext<Input>()
  const { t } = useTranslation()

  const dataConsent = form.watch('dataConsent')
  const testPeriodConsent = form.watch('testPeriodConsent')
  const individualConsent = form.watch('individualConsent')
  const creatorRelation = form.watch('creatorRelation')
  const showIndividualConsent = creatorRelation !== 'self'

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h4 className="text-lg font-semibold">{t('addIndividualPage.dataConsent.title')}</h4>
        <p>{t('addIndividualPage.dataConsent.description1')}</p>
        <p>{t('addIndividualPage.dataConsent.description2')}</p>
        <p>{t('addIndividualPage.dataConsent.description3')}</p>
      </div>
      <div className="space-y-4">
        <label className="flex items-start gap-3 cursor-pointer group">
          <Checkbox
            checked={dataConsent}
            onCheckedChange={checked =>
              form.setValue(
                'dataConsent',
                checked === true ? true : (undefined as unknown as true),
                {
                  shouldValidate: true,
                },
              )
            }
          />
          <span className="text-sm leading-snug">
            {t('addIndividualPage.dataConsent.consentLabel')}
          </span>
        </label>
        {form.formState.errors.dataConsent && (
          <p className="text-error text-sm">{form.formState.errors.dataConsent.message}</p>
        )}

        <label className="flex items-start gap-3 cursor-pointer group">
          <Checkbox
            checked={testPeriodConsent}
            onCheckedChange={checked =>
              form.setValue(
                'testPeriodConsent',
                checked === true ? true : (undefined as unknown as true),
                { shouldValidate: true },
              )
            }
          />
          <span className="text-sm leading-snug">
            {t('addIndividualPage.dataConsent.testPeriodConsentLabel')}
          </span>
        </label>
        {form.formState.errors.testPeriodConsent && (
          <p className="text-error text-sm">{form.formState.errors.testPeriodConsent.message}</p>
        )}

        {showIndividualConsent && (
          <>
            <label className="flex items-start gap-3 cursor-pointer group">
              <Checkbox
                checked={individualConsent}
                onCheckedChange={checked =>
                  form.setValue('individualConsent', checked === true, { shouldValidate: true })
                }
              />
              <span className="text-sm leading-snug">
                {t('addIndividualPage.dataConsent.individualConsentLabel')}
              </span>
            </label>
            {form.formState.errors.individualConsent && (
              <p className="text-error text-sm">
                {form.formState.errors.individualConsent.message}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const SubmitStep = () => {
  const form = useFormContext<Input>()
  const { t } = useTranslation()
  const { data: municipalities = [] } = useMunicipalities()

  const municipalityName =
    municipalities.find(m => m.id === form.getValues('municipality'))?.name ??
    form.getValues('municipality')

  return (
    <div className="flex flex-col gap-4 [&_>_div]:space-y-1">
      <div>
        <h4 className="text-text-secondary text-sm">{t('defaultFormInputs.fullName.label')}</h4>
        <p className="font-semibold">
          {form.getValues('firstName')} {form.getValues('lastName')}
        </p>
      </div>
      <div>
        <h4 className="text-text-secondary text-sm">{t('defaultFormInputs.pid.label')}</h4>
        <p className="font-semibold">{form.getValues('pid')}</p>
      </div>
      <div>
        <h4 className="text-text-secondary text-sm">{t('formInputs.municipality.label')}</h4>
        <p className="font-semibold">{municipalityName}</p>
      </div>
      {form.getValues('gender') && (
        <div>
          <h4 className="text-text-secondary text-sm">
            {t('defaultFormInputs.gender.label')} {'('}
            {t('global.optional')}
            {')'}
          </h4>
          <p className="font-semibold">
            {t('defaultFormOptions.gender', { context: form.getValues('gender') })}
          </p>
        </div>
      )}
    </div>
  )
}

const PersonalInformationStep = () => {
  const form = useFormContext<Input>()
  const { t } = useTranslation()
  const { data: municipalities = [] } = useMunicipalities()

  const gender = form.watch('gender')
  const municipality = form.watch('municipality')
  const creatorRelation = form.watch('creatorRelation')
  const creatorRelationOther = form.watch('creatorRelationOther')
  const creatorIsGuardian = form.watch('creatorIsGuardian') ?? false
  const creatorIsLegalRepresentative = form.watch('creatorIsLegalRepresentative') ?? false

  return (
    <div>
      <div className="mb-14 space-y-4">
        <h4 className="text-lg font-semibold">{t('addIndividualPage.informationAlert.title')}</h4>
        <p>{t('addIndividualPage.informationAlert.description')}</p>
        <p>{t('addIndividualPage.informationAlert.description2')}</p>
      </div>
      <FieldGroup>
        <FieldGroup className="flex flex-row gap-4">
          <Field>
            <FieldLabel htmlFor="firstName">{t('defaultFormInputs.firstName.label')}</FieldLabel>
            <Input
              autoComplete="off"
              id="firstName"
              placeholder={t('defaultFormInputs.firstName.placeholder')}
              {...form.register('firstName')}
            />
            <FieldError>{form.formState.errors.firstName?.message}</FieldError>
          </Field>
          <Field>
            <FieldLabel htmlFor="lastName">{t('defaultFormInputs.lastName.label')}</FieldLabel>
            <Input
              autoComplete="off"
              id="lastName"
              placeholder={t('defaultFormInputs.lastName.placeholder')}
              {...form.register('lastName')}
            />
            <FieldError>{form.formState.errors.lastName?.message}</FieldError>
          </Field>
        </FieldGroup>

        <FieldGroup className="flex flex-row gap-4">
          <Field>
            <FieldLabel>{t('defaultFormInputs.pid.label')}</FieldLabel>
            <PidInput name="pid" />
            <FieldDescription>{t('defaultFormInputs.pid.description')}</FieldDescription>
          </Field>

          <Field>
            <FieldLabel>
              {t('defaultFormInputs.gender.label')} {'('}
              {t('global.optional')}
              {')'}
            </FieldLabel>
            <Select
              {...form.register('gender')}
              onValueChange={value => form.setValue('gender', value as 'male')}
              value={gender}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('defaultFormInputs.gender.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="male">{t('defaultFormOptions.gender_male')}</SelectItem>
                  <SelectItem value="female">{t('defaultFormOptions.gender_female')}</SelectItem>
                  <SelectItem value="other">{t('defaultFormOptions.gender_other')}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <FieldError>{form.formState.errors.gender?.message}</FieldError>
          </Field>
        </FieldGroup>

        <Field>
          <FieldLabel>{t('formInputs.municipality.label')}</FieldLabel>
          <MunicipalityInput
            {...form.register('municipality')}
            municipalities={municipalities}
            onValueChange={value => form.setValue('municipality', value)}
            value={municipality}
          />
          <FieldError>{form.formState.errors.municipality?.message}</FieldError>
        </Field>

        <RelationFieldGroup
          idPrefix="creator"
          includeOther
          isGuardian={creatorIsGuardian}
          isLegalRepresentative={creatorIsLegalRepresentative}
          onIsGuardianChange={value => form.setValue('creatorIsGuardian', value)}
          onIsLegalRepresentativeChange={value =>
            form.setValue('creatorIsLegalRepresentative', value)
          }
          onRelationChange={value =>
            form.setValue('creatorRelation', value, { shouldValidate: true })
          }
          onRelationOtherChange={value =>
            form.setValue('creatorRelationOther', value, { shouldValidate: true })
          }
          perspective="first-person"
          relation={creatorRelation}
          relationDescription={t('relation.creatorDescription')}
          relationError={form.formState.errors.creatorRelation?.message}
          relationLabel={t('relation.creatorLabel')}
          relationOther={creatorRelationOther}
          relationOtherError={form.formState.errors.creatorRelationOther?.message}
        />
      </FieldGroup>
    </div>
  )
}

export const Route = createFileRoute('/add-individual')({
  component: AddIndividualPage,
})
